<script lang="ts">
	import type { Cost, Distance, Duration, RouteSelectorType } from '$lib/components/route/routeSelector/types';
	import type { Snippet } from 'svelte';
	import Badge from '$lib/components/atoms/badge/Badge.svelte';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Icon from '$lib/components/icons/Icon.svelte';
	import * as c from '$lib/i18n/computedMessages';
	import { preventDefaultAndPropagation } from '$lib/utils/preventDefaultAndPropagation';
	import * as m from '$paraglide/messages';
	import { quartIn, quartOut } from 'svelte/easing';
	import { fly } from 'svelte/transition';
	import ListGroup from './ListGroup.svelte';

	interface Props {
		routes?: RouteSelectorType[];
		selectedRouteIndex?: number;
		summary: Snippet;
		durationColumn: Snippet<[Duration, boolean, boolean]>;
		distanceColumn: Snippet<[Distance]>;
		consumptionColumn: Snippet<[number]>;
		emissionsColumn: Snippet<[number]>;
		costColumn: Snippet<[Cost[]]>;
		collapsed?: boolean;
		errorType?: 'noRoad' | 'noAddress' | undefined;
		onClickOnSummary?: () => void;
		onIndexChange?: (index: number) => void;
		onToggleRouteSelector?: (isCollapsed: boolean) => void;
	}

	let {
		routes,
		selectedRouteIndex,
		summary,
		durationColumn,
		distanceColumn,
		consumptionColumn,
		emissionsColumn,
		costColumn,
		errorType,
		collapsed = $bindable(false),
		onClickOnSummary,
		onIndexChange,
		onToggleRouteSelector,
	}: Props = $props();

	let selectedId = $state<string | undefined>(routes?.[selectedRouteIndex ?? 0]?.identifier ?? undefined);

	function handleClick(e: MouseEvent | KeyboardEvent, index?: number) {
		e.stopPropagation();
		if (!routes || routes.length <= 1) return;

		if (collapsed) {
			onToggleRouteSelector?.(false);
			return;
		}

		selectedId = undefined;
		selectedId = routes?.[index ?? 0]?.identifier ?? '';
		onIndexChange?.(index ?? 0);
	}

	function handleKeyboard(e: KeyboardEvent, index?: number) {
		if (e.key === 'Enter') {
			handleClick(e, index);
		}
	}

	function getTransitionProperties(index: number, type: 'in' | 'out') {
		const isTransitionTypeIn = type === 'in';

		return {
			duration: isTransitionTypeIn ? index * 50 : 80,
			delay: isTransitionTypeIn ? index * 5 : 0,
			y: 10,
			opacity: 1,
			easing: isTransitionTypeIn ? quartIn : quartOut,
		};
	}
</script>

<ListGroup
	list={routes}
	error={errorType}
	class={[collapsed && 'pb-1']}
>
	{#snippet header()}
		<!-- waitpoints and badges area -->
		{@render summary?.()}

		<Button
			icon="edit"
			variant="ghost"
			class="grid-area-[button] bg-neutral-weakest hover:bg-neutral-weak text-sm top-0 right-2 shadow-smooth-bottom-2 rd-full w-9 h-9 p-0"
			ariaLabel={m.itinerary__edit()}
			dataTestId="edit-route"
			onclick={onClickOnSummary}
		/>
	{/snippet}
	{#snippet listItem(route)}
		{#key route.identifier}
			{@const isSelected = route.identifier === selectedId}
			{#if !collapsed || (collapsed && isSelected)}
				<button
					in:fly={getTransitionProperties(route.index, 'in')}
					out:fly={getTransitionProperties(route.index, 'out')}
					class={[
						'route-group-row group rd-3 text-coloredneutral grid p-2 m-0 text-center appearance-none border-none bg-transparent w-full cursor-pointer',
						isSelected && 'highlight-route-group relative text-coloredneutral-weakest',
						isSelected && collapsed && 'mb-3 is-collapsed',
						!isSelected && !collapsed && 'hover:bg-neutral-soft hover:rd-3',
						isSelected && collapsed && routes && routes.length > 1 && 'with-cards',
					]}
					type="button"
					aria-hidden={!isSelected && !collapsed}
					aria-current={isSelected}
					data-testid={`route-group-row-${route.index}`}
					onclick={e => handleClick(e, route.index)}
					onmousedown={preventDefaultAndPropagation}
					onpointerdown={preventDefaultAndPropagation}
					onkeydown={e => handleKeyboard(e, route.index)}
				>
					<span data-testid="duration" aria-label={m.route__route_selector__column_duration()}>
						{@render durationColumn(route.duration, route.isDiscovery && !collapsed, isSelected)}
					</span>
					<span data-testid="cost" aria-label={m.route__route_selector__column_cost()}>
						{@render costColumn(route?.cost)}
					</span>
					<span data-testid="distance" aria-label={m.route__route_selector__column_distance()}>
						{@render distanceColumn(route.distance)}
					</span>
					{#if route.isEv}
						<span data-testid="consumption" class="hidden sm:block" role="cell" aria-label={m.route__route_selector__column_electricity_consumption()}>
							{@render consumptionColumn(route.consumption)}
						</span>
					{:else}
						<span data-testid="emissions" class="hidden sm:block" role="cell" aria-label={m.route__route_selector__column_emission()}>
							{@render emissionsColumn(route.emissions)}
						</span>
					{/if}
					<span>
						<Icon
							icon="chevron-right"
							class={[
								'w-4 h-full text-coloredneutral-weak',
								collapsed && 'rotate-90 animate-[rotate-90_0.15_ease-in-out] text-coloredneutral-weakest',
								!collapsed && 'hover:text-coloredneutral-weakest',
								!isSelected && 'group-hover:text-primary',
							]}
							tag="span"
						/>
					</span>
				</button>
			{/if}
			{#if (route.isDiscovery || route.trafficImpactDuration) && collapsed && isSelected}
				<!-- badge expanded -->
				<div class={[
					'px-2 pb-1 cursor-pointer flex gap-2 opacity-0 transition-opacity ease-linear duration-150 delay-50',
					collapsed && isSelected && 'opacity-100 transition-opacity ease-linear duration-150 delay-0',
				]}>
					{#if route.trafficImpactDuration}
						{@const hours = route.trafficImpactDuration?.hours ? `${route.trafficImpactDuration.hours} h` : ''}
						{@const minutes = route.trafficImpactDuration?.minutes ? `${route.trafficImpactDuration.minutes} min` : ''}
						<Badge
							icon="traffic-jam"
							isSquare
							size="sm"
							text={`${hours} ${minutes}`}
							class="font-size-3.5 font-400 px-1.5 py-1.75 bg-neutral-weak w-5 h-6"
							colorClasses="text-traffic-saturated!"
							labelClasses="pl-1"
						/>
					{/if}
					{#if route.isDiscovery}
						<Badge
							icon="scenic-road"
							isSquare
							size="sm"
							text={c.routeBadge('discovery')}
							class="font-size-3.5 font-400 px-0 bg-discovery-weak w-5 h-6"
							colorClasses="text-discovery!"
							labelClasses="pl-0.5"
						/>
					{/if}
				</div>
			{/if}
		{/key}
	{/snippet}
	{#snippet errorItem()}
		<li class="py-2">
			<div class="flex items-center gap-x-4 pl-2">
				<Icon icon={errorType === 'noAddress' ? 'no-address' : 'no-road'} class=" w-11.25 h-11.25" />
				<p class="text-coloredneutral-soft font-400 font-size-3.5 line-height-4 m-0">
					{errorType === 'noAddress' ? m.route__route_selector_no_address() : m.route__route_selector_no_road()}
					<br />{m.route__route_selector_error_message()}
				</p>
			</div>
		</li>
	{/snippet}
	{#snippet emptyListItem()}
		<div class="flex flex-row items-center gap-2 text-sm p-2">
			<Icon icon="no-road" class=" w-11.25 h-11.25" />
			<p class="text-coloredneutral-soft font-400 m-0 flex flex-wrap">
				{m.route__route_selector_no_road()} <br />{m.route__route_selector_error_message()}
			</p>
		</div>
	{/snippet}
</ListGroup>
<style>
	.highlight-route-group {
		background: radial-gradient(46.53% 223.24% at 1.39% 10.23%, #1D3140 0%, #3A4F5F 100%);

		&.with-cards {
				&::before,
				&::after {
					--card-y-position: 0;
					--uno:  inset-0 w-full h-44px rd-3 inline absolute bg-transparent;
					content: '';
					will-change: transform;
					transition: transform 150ms 130ms cubic-bezier(.01,.75,.32,1.14);
					transform: translate3d(0, var(--card-y-position, 0), var(--card-z-position, -1px));
				}
				&::before {
					--card-z-position: -1px;
					--uno: bg-coloredneutral-softest z--14;
				}
				&::after {
					--card-z-position: -2px;
					--uno: bg-coloredneutral-weak z--15;

				}

			&.is-collapsed {
				--top-card-y-position: 4px;
				--bottom-card-y-position: 8px;
				&::before,
				&::after {
					transition: transform 150ms cubic-bezier(.01,.75,.32,1.14);
				}

				&::before {
					--card-y-position: 4px;
					--uno: bg-coloredneutral-softest z--14;

				}
				&::after {
					--card-y-position: 8px;
					--uno: bg-coloredneutral-weak z--15;

				}

				&:hover {
					transition: transform 100ms cubic-bezier(.01,.75,.32,1.14);

					&::before {
						--card-y-position: 5px;
					}
					&::after {
						--card-y-position: 10px;
					}
				}
			}
		}
	}

	.route-group-row {
		grid-template-columns: 1.5fr minmax(0,1fr) minmax(0,1fr) 0.5fr;
		@media (min-width: 640px) {
			grid-template-columns: 1.5fr minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) 0.5fr;
		}
	}
</style>
